import React from 'react';

const BG_BLOCKS = [40, 110, 175, 240, 310, 380, 450, 520, 590, 660, 730, 805, 880, 955, 1030, 1100, 1175, 1250, 1320, 1395, 1470, 1540];

/**
 * SkylineBuildings — the front + back layers of the hand-drawn city silhouette.
 * Each building is bespoke; this file groups them together so CitySkyline.jsx
 * can stay readable.
 */
const SkylineBuildings = ({ stroke, accent, accentAlt }) => (
  <>
    {/* ---- back layer of distant blocks ---- */}
    <g stroke={stroke} strokeWidth="0.7" strokeOpacity="0.35" fill="none">
      {BG_BLOCKS.map((x, i) => {
        const h = 28 + ((i * 13) % 36);
        return (
          <g key={`bg${i}`}>
            <rect x={x} y={150 - h} width="50" height={h} fill={stroke} fillOpacity="0.05" />
            <line x1={x} y1={150 - h} x2={x + 50} y2={150 - h} strokeOpacity="0.5" />
          </g>
        );
      })}
      <path d="M 0 152 Q 250 130 500 152 Q 800 130 1100 152 Q 1400 130 1600 152" />
    </g>

    {/* ---- front skyline ---- */}
    <g stroke={stroke} strokeWidth="1.6" strokeOpacity="0.95" fill={stroke} fillOpacity="0.05" filter="url(#cs-rough)">

      {/* Wide block w/ pyramid top */}
      <path d="M 30 280 L 30 170 L 90 130 L 150 170 L 150 280 Z" />
      <line x1="60" y1="280" x2="60" y2="170" strokeOpacity="0.4" />
      <line x1="120" y1="280" x2="120" y2="170" strokeOpacity="0.4" />
      <line x1="30" y1="200" x2="150" y2="200" strokeOpacity="0.4" />
      <line x1="30" y1="240" x2="150" y2="240" strokeOpacity="0.4" />

      {/* Classic skyscraper w/ stepped crown */}
      <path d="M 170 280 L 170 100 L 240 100 L 240 80 L 250 80 L 250 60 L 260 60 L 260 100 L 230 100 L 230 280 Z" />
      {[180, 200, 220].map(x => <line key={x} x1={x} y1="280" x2={x} y2="100" strokeOpacity="0.35" />)}
      <line x1="250" y1="50" x2="250" y2="20" strokeWidth="1.2" />
      <circle cx="250" cy="16" r="2" fill={stroke} />

      {/* Church / clock tower */}
      <path d="M 285 280 L 285 180 L 310 180 L 310 150 L 290 130 L 295 100 L 305 100 L 310 130 L 290 150 L 310 150 L 310 180 L 335 180 L 335 280 Z" />
      <circle cx="310" cy="200" r="6" fill="none" strokeWidth="1.2" />
      <line x1="310" y1="195" x2="310" y2="200" strokeWidth="1" />
      <line x1="310" y1="200" x2="313" y2="200" strokeWidth="1" />
      <line x1="300" y1="92" x2="320" y2="92" strokeWidth="1.4" />

      {/* Mid block */}
      <path d="M 360 280 L 360 150 L 430 150 L 430 280 Z" />
      {[372, 388, 404, 420].map(x => <line key={x} x1={x} y1="280" x2={x} y2="150" strokeOpacity="0.3" />)}
      {[170, 200, 230, 260].map(y => <line key={y} x1="360" y1={y} x2="430" y2={y} strokeOpacity="0.3" />)}

      {/* Dome capitol */}
      <path d="M 450 280 L 450 175 L 540 175 L 540 280 Z" />
      <path d="M 460 175 Q 495 130 530 175" />
      <line x1="495" y1="138" x2="495" y2="118" />
      <circle cx="495" cy="115" r="3" fill={stroke} />
      {[460, 470, 480, 490, 500, 510, 520, 530].map(x => (
        <line key={x} x1={x} y1="200" x2={x} y2="280" strokeOpacity="0.35" />
      ))}
      <line x1="450" y1="195" x2="540" y2="195" strokeWidth="1.4" />

      {/* Slim spire skyscraper w/ flag */}
      <path d="M 560 280 L 560 90 L 610 90 L 610 60 L 615 60 L 615 280 Z" />
      {[570, 580, 590, 600].map(x => <line key={x} x1={x} y1="280" x2={x} y2="90" strokeOpacity="0.3" />)}
      <line x1="612" y1="60" x2="612" y2="10" strokeWidth="1.1" />
      <g className="cs-flag" transform="translate(612 12)">
        <polygon points="0,0 12,3 0,6" fill={accentAlt} stroke="none" />
      </g>

      {/* Wide office block */}
      <path d="M 640 280 L 640 140 L 740 140 L 740 280 Z" />
      {[655, 670, 685, 700, 715, 730].map(x => <line key={x} x1={x} y1="280" x2={x} y2="140" strokeOpacity="0.3" />)}
      {[160, 180, 200, 220, 240, 260].map(y => <line key={y} x1="640" y1={y} x2="740" y2={y} strokeOpacity="0.3" />)}

      {/* Pencil tower (very tall thin) */}
      <path d="M 760 280 L 760 50 L 790 50 L 790 280 Z" />
      {[770, 780].map(x => <line key={x} x1={x} y1="280" x2={x} y2="50" strokeOpacity="0.3" />)}
      <line x1="775" y1="50" x2="775" y2="20" />
      <circle cx="775" cy="17" r="2" fill={accent} />

      {/* Water-tank tower */}
      <g transform="translate(815 0)">
        <ellipse cx="0" cy="60" rx="22" ry="8" fill="none" />
        <path d="M -22 60 L -22 90 L 22 90 L 22 60" />
        <line x1="-18" y1="90" x2="-22" y2="125" />
        <line x1="18" y1="90" x2="22" y2="125" />
        <line x1="-5" y1="90" x2="-5" y2="125" />
        <line x1="5" y1="90" x2="5" y2="125" />
        <line x1="-22" y1="125" x2="22" y2="125" />
        <line x1="-15" y1="100" x2="15" y2="125" strokeOpacity="0.45" />
        <line x1="15" y1="100" x2="-15" y2="125" strokeOpacity="0.45" />
        <line x1="-22" y1="125" x2="-22" y2="280" />
        <line x1="22" y1="125" x2="22" y2="280" />
        <line x1="0" y1="125" x2="0" y2="280" strokeOpacity="0.45" />
      </g>

      {/* Cathedral with two spires */}
      <path d="M 870 280 L 870 200 L 930 200 L 930 280 Z" />
      <polygon points="880,200 880,160 885,160 885,150 875,150 875,140 882,140 882,130 887,130 887,140 894,140 894,150 884,150 884,160 889,160 889,200" />
      <polygon points="915,200 915,165 920,165 920,155 910,155 910,145 917,145 917,135 922,135 922,145 929,145 929,155 919,155 919,165 924,165 924,200" />
      <circle cx="900" cy="220" r="8" fill="none" strokeWidth="1.2" />
      <line x1="900" y1="215" x2="900" y2="225" strokeOpacity="0.45" />
      <line x1="895" y1="220" x2="905" y2="220" strokeOpacity="0.45" />

      {/* Tall flat-top skyscraper */}
      <path d="M 950 280 L 950 70 L 1010 70 L 1010 280 Z" />
      {[962, 974, 986, 998].map(x => <line key={x} x1={x} y1="280" x2={x} y2="70" strokeOpacity="0.3" />)}
      {[90, 110, 130, 150, 170, 190, 210, 230, 250].map(y => <line key={y} x1="950" y1={y} x2="1010" y2={y} strokeOpacity="0.25" />)}
      <line x1="980" y1="70" x2="980" y2="32" />
      <line x1="975" y1="40" x2="985" y2="40" />

      {/* Stepped pyramid building */}
      <path d="M 1025 280 L 1025 220 L 1070 220 L 1070 180 L 1100 180 L 1100 140 L 1135 140 L 1135 180 L 1165 180 L 1165 220 L 1210 220 L 1210 280 Z" />
      <line x1="1117" y1="140" x2="1117" y2="100" />
      <polygon points="1117,100 1122,98 1117,95 1112,98" fill={stroke} />

      {/* Ferris wheel */}
      <g>
        <circle cx="1260" cy="160" r="52" fill="none" strokeWidth="1.4" />
        <circle cx="1260" cy="160" r="4" fill={stroke} />
        <g className="cs-wheel" style={{ transformOrigin: '1260px 160px' }}>
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(deg => {
            const rad = (deg * Math.PI) / 180;
            const x = 1260 + Math.cos(rad) * 52;
            const y = 160 + Math.sin(rad) * 52;
            return (
              <g key={deg}>
                <line x1="1260" y1="160" x2={x} y2={y} strokeOpacity="0.5" />
                <rect x={x - 4} y={y - 3} width="8" height="6" fill="none" />
              </g>
            );
          })}
        </g>
        <line x1="1230" y1="220" x2="1260" y2="160" />
        <line x1="1290" y1="220" x2="1260" y2="160" />
        <line x1="1225" y1="225" x2="1295" y2="225" strokeWidth="1.5" />
      </g>

      {/* Modern apartment block */}
      <path d="M 1325 280 L 1325 130 L 1395 130 L 1395 280 Z" />
      {[1340, 1355, 1370, 1385].map(x => <line key={x} x1={x} y1="280" x2={x} y2="130" strokeOpacity="0.3" />)}
      {[150, 170, 190, 210, 230, 250, 270].map(y => <line key={y} x1="1325" y1={y} x2="1395" y2={y} strokeOpacity="0.3" />)}

      {/* Factory with two smokestacks */}
      <path d="M 1415 280 L 1415 200 L 1460 200 L 1460 180 L 1475 180 L 1475 200 L 1500 200 L 1500 280 Z" />
      <rect x="1430" y="140" width="9" height="60" fill="none" />
      <rect x="1428" y="136" width="13" height="6" fill="none" />
      <rect x="1480" y="155" width="9" height="45" fill="none" />
      <rect x="1478" y="151" width="13" height="6" fill="none" />
      {[1415, 1425, 1435, 1445, 1490].map(x => (
        <path key={x} d={`M ${x} 200 L ${x + 5} 195 L ${x + 10} 200`} strokeOpacity="0.45" />
      ))}

      {/* Final two slim skyscrapers */}
      <path d="M 1520 280 L 1520 100 L 1555 100 L 1555 280 Z" />
      {[1530, 1540, 1550].map(x => <line key={x} x1={x} y1="280" x2={x} y2="100" strokeOpacity="0.3" />)}
      <line x1="1538" y1="100" x2="1538" y2="65" />
      <line x1="1538" y1="70" x2="1545" y2="62" />
      <circle cx="1545" cy="60" r="2" fill={accentAlt} />

      <path d="M 1575 280 L 1575 140 L 1600 140 L 1600 280 Z" />
      {[1583, 1591].map(x => <line key={x} x1={x} y1="280" x2={x} y2="140" strokeOpacity="0.3" />)}
    </g>
  </>
);

export default SkylineBuildings;
